import { useEffect, useMemo, useState } from 'react';
import type { RootState } from '@/store';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { USER_ROLE } from '@/config/constants';
import {
  useCreateProductMutation,
  useGetProductsQuery,
} from '@/apis/extended/productApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { ContinueMortgageFileButton } from '@/components/CommercialLoanApplication/ContinueMortgageFileButton';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { ProductDetailProgress } from '@/components/ProductDetail/ProductDetailProgress';
import { AddPropertyDialog } from '@/components/Property/AddPropertyDialog';
import { PropertyCard } from '@/components/Property/PropertyCard';
import { useProperties } from '@/hooks/useProperties';
import {
  PropertyFilter,
  PropertySort,
  setFilter,
  setSort,
} from '@/store/slices/propertyUiSlice';
import { resolvePortfolioProgressView } from '@/utils/portfolioProgress';
import { startPropertyTutorial } from '@/utils/startPropertyTutorial';

const FILTER_OPTIONS: Array<{ value: PropertyFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'dscr', label: 'Investment' },
];

const SORT_OPTIONS: Array<{ value: PropertySort; label: string }> = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'stage', label: 'Stage' },
  { value: 'price', label: 'Price' },
];

const CHROME_EXTENSION_URL =
  process.env.VITE_EXTENSION_URL ||
  'https://chromewebstore.google.com/detail/unbeatable-extension/effibccnihcalkgiimempmhiabigndfl';

export const PropertiesGridPage = () => {
  const theme = useTheme();
  const isCompact = useMediaQuery('(max-width: 640px)');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore<RootState>();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useSelector((s: RootState) => s.propertyUi.filter);
  const sort = useSelector((s: RootState) => s.propertyUi.sort);
  const [addOpen, setAddOpen] = useState(false);
  const [createProduct, { isLoading: isStartingTutorial }] =
    useCreateProductMutation();

  // When the URL query changes (deep link, back/forward), apply it to Redux.
  // Do not depend on `filter` here: after a chip click Redux updates before the
  // URL does; re-running with a stale ?filter=purchase would dispatch purchase
  // and undo the click.
  useEffect(() => {
    const qsFilter = searchParams.get('filter') as PropertyFilter | null;
    if (!qsFilter || !FILTER_OPTIONS.some((f) => f.value === qsFilter)) return;
    const reduxFilter = store.getState().propertyUi.filter;
    if (reduxFilter !== qsFilter) {
      dispatch(setFilter(qsFilter));
    }
  }, [searchParams, dispatch, store]);

  // Mirror Redux filter to the URL for shareability. Read the store in the
  // effect so this runs after the URL→Redux effect above updates state in the
  // same tick (avoids stripping ?filter= on first paint).
  useEffect(() => {
    const f = store.getState().propertyUi.filter;
    if (f === 'all') {
      if (searchParams.has('filter')) {
        const next = new URLSearchParams(searchParams);
        next.delete('filter');
        setSearchParams(next, { replace: true });
      }
      return;
    }
    if (searchParams.get('filter') !== f) {
      const next = new URLSearchParams(searchParams);
      next.set('filter', f);
      setSearchParams(next, { replace: true });
    }
  }, [filter, searchParams, setSearchParams, store]);

  // Auto-open the add dialog if directed via /properties/add.
  useEffect(() => {
    if (searchParams.get('add') === '1') {
      setAddOpen(true);
    }
  }, [searchParams]);

  const { properties, allProperties, isLoading, sellerProductsById } =
    useProperties();
  const { data: currentUser } = useGetCurrentUserQuery();
  const isSeller = currentUser?.role === USER_ROLE.SELLER;
  const { data: rawProducts } = useGetProductsQuery({}, { skip: isSeller });
  const productsById = useMemo(() => {
    if (isSeller) return sellerProductsById;
    const map = new Map<
      string,
      NonNullable<typeof rawProducts>['products'][number]
    >();
    rawProducts?.products?.forEach((p) => map.set(p._id, p));
    return map;
  }, [isSeller, sellerProductsById, rawProducts?.products]);

  const portfolioProgress = useMemo(
    () =>
      isSeller
        ? null
        : resolvePortfolioProgressView(
            filter,
            allProperties,
            rawProducts?.products ?? [],
            currentUser
          ),
    [isSeller, filter, allProperties, rawProducts?.products, currentUser]
  );

  if (isLoading) {
    return (
      <Container sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const isEmpty = allProperties.length === 0;

  const pageTitle = isSeller ? 'My Listings' : 'My Properties';
  const emptyTitle = isSeller
    ? 'No assigned listings yet'
    : 'Save your first property';
  const emptyBody = isSeller
    ? 'When a buyer sends you a contract for a property, it will appear here.'
    : 'Add an address to start exploring rates, payments, and your personalised next steps—or save a listing from any site with our Chrome extension.';

  const handleStartGuidedTour = async () => {
    await startPropertyTutorial({
      createProduct,
      existingProducts: rawProducts?.products ?? [],
      dispatch,
      navigate,
    });
  };

  const filterChips = !isEmpty && !isSeller && (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      {FILTER_OPTIONS.map((opt) => (
        <Chip
          key={opt.value}
          label={opt.label}
          color={filter === opt.value ? 'primary' : 'default'}
          variant={filter === opt.value ? 'filled' : 'outlined'}
          onClick={() => dispatch(setFilter(opt.value))}
          sx={{ fontWeight: 600 }}
        />
      ))}
    </Stack>
  );

  const filterSelect = !isEmpty && !isSeller && (
    <Select
      size="small"
      value={filter}
      onChange={(e) => dispatch(setFilter(e.target.value as PropertyFilter))}
      sx={{ minWidth: 160 }}
    >
      {FILTER_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  const sortSelect = !isEmpty && (
    <Select
      size="small"
      value={sort}
      onChange={(e) => dispatch(setSort(e.target.value as PropertySort))}
      sx={{ minWidth: 200 }}
    >
      {SORT_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          Sort: {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  const addButton = !isSeller ? (
    <Button
      variant="contained"
      startIcon={<i className="fa-light fa-plus" />}
      onClick={() => setAddOpen(true)}
    >
      Add Property
    </Button>
  ) : null;

  return (
    <Container sx={{ py: 4 }} className="taste-rise">
      {isCompact ? (
        <>
          <TastePageHeader
            title={pageTitle}
            subtitle={
              isSeller
                ? 'Listings assigned to you from buyer contracts.'
                : 'Rates, payments, and next steps for each saved address.'
            }
            actions={addButton}
          />

          {!isEmpty && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3,
              }}
            >
              {filterSelect}
              {sortSelect}
            </Box>
          )}
        </>
      ) : (
        <TastePageHeader
          title={pageTitle}
          subtitle={
            isSeller
              ? 'Listings assigned to you from buyer contracts.'
              : 'Rates, payments, and next steps for each saved address.'
          }
          actions={
            <>
              {filterChips}
              {sortSelect}
              {addButton}
            </>
          }
        />
      )}

      {isEmpty ? (
        <Box className="taste-empty-state" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {emptyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {emptyBody}
          </Typography>
          {!isSeller && (
            <Stack spacing={1.5} alignItems="center">
              <Button variant="contained" onClick={() => setAddOpen(true)}>
                Add a property
              </Button>
              <Button
                variant="outlined"
                href={CHROME_EXTENSION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get the Chrome extension
              </Button>
              <Button
                variant="text"
                disabled={isStartingTutorial}
                onClick={() => void handleStartGuidedTour()}
              >
                {isStartingTutorial ? 'Starting tour…' : 'Start guided tour'}
              </Button>
            </Stack>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gridAutoRows: '1fr',
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {portfolioProgress ? (
            <Box
              className="taste-panel taste-rise"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                height: '100%',
                minWidth: 0,
                p: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {portfolioProgress.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {portfolioProgress.subtitle}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ProductDetailProgress
                  checkItems={portfolioProgress.items}
                  showTopMargin={false}
                  fillHeight
                  compact
                  footer={
                    portfolioProgress.footerProductId &&
                    portfolioProgress.footerProduct &&
                    portfolioProgress.kind !== 'refinance' ? (
                      <ContinueMortgageFileButton
                        productId={portfolioProgress.footerProductId}
                        product={portfolioProgress.footerProduct}
                      />
                    ) : null
                  }
                />
              </Box>
            </Box>
          ) : null}
          {properties.map((p) => {
            const product = productsById.get(p.productId);
            if (!product) return null;
            return (
              <PropertyCard
                key={p.id}
                property={p}
                product={product}
                hideDelete={isSeller}
              />
            );
          })}
        </Box>
      )}

      {!isSeller && (
        <AddPropertyDialog
          open={addOpen}
          onClose={() => {
            setAddOpen(false);
            if (searchParams.get('add') === '1') {
              const next = new URLSearchParams(searchParams);
              next.delete('add');
              setSearchParams(next, { replace: true });
            }
          }}
          onCreated={(id) => {
            setAddOpen(false);
            navigate(`/properties/${id}`);
          }}
        />
      )}
    </Container>
  );
};

export default PropertiesGridPage;
